import React from 'react';
import { Input, InputNumber, Select, AutoComplete, Button } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

// normalized view of rows for comparison / persisting: drops rows without a
// field name and strips empty cells
const stripEmpty = (rows) =>
    (rows || [])
        .filter((r) => r.field)
        .map((r) => {
            const clean = {};
            Object.keys(r).forEach((k) => {
                if (k !== 'key' && r[k] !== undefined && r[k] !== null && r[k] !== '') {
                    clean[k] = r[k];
                }
            });
            return clean;
        });

/**
 * Generic editable field-mapping list (Field → Action → …) rendered as a calm,
 * composed grid instead of a table.
 * columns: [{key,title,type:'field'|'text'|'number'|'select',options,width}]
 *  - 'field' renders an AutoComplete fed with the form's field names
 *
 * Rows live in local state so freshly added (still empty) rows survive the
 * round-trip through the config object; external config changes (e.g. via
 * code view) re-sync the local state.
 */
const FieldsEditor = ({ title, rows: propRows, columns, formFields, onChange, addLabel }) => {
    const [rows, setRows] = React.useState(propRows || []);

    React.useEffect(() => {
        if (JSON.stringify(stripEmpty(propRows)) !== JSON.stringify(stripEmpty(rows))) {
            setRows(propRows || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propRows]);

    const apply = (next, propagate) => {
        setRows(next);
        if (propagate) {
            onChange(next);
        }
    };

    const update = (i, key, value) => {
        apply(rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)), true);
    };
    const remove = (i) => apply(rows.filter((_, idx) => idx !== i), true);
    // a brand-new row has no field name yet, so it would be stripped on save -
    // keep it local only until something is entered
    const add = () => apply([...rows, {}], false);

    const renderCell = (c, r, i) => {
        const v = r[c.key];
        // only show cells that apply to the chosen action, so rows aren't
        // cluttered with empty inputs that do nothing (e.g. Value for "random")
        const action = r.action;
        const applies =
            action == null ? true :
            c.key === 'value' ? action === 'constant' :
            c.key === 'personAttribute' ? action === 'random' :
            c.key === 'size' ? action !== 'clear' :
            true;
        if (!applies) {
            return <span style={{ color: 'var(--sc-ink-3)', paddingInlineStart: 8 }} title="not used for this action">—</span>;
        }
        if (c.type === 'number') {
            return <InputNumber value={v} onChange={(val) => update(i, c.key, val)} style={{ width: '100%' }} />;
        }
        if (c.type === 'select') {
            return (
                <Select
                    value={v}
                    allowClear
                    options={(c.options || []).map((o) => ({ value: o }))}
                    onChange={(val) => update(i, c.key, val)}
                    style={{ width: '100%' }}
                />
            );
        }
        if (c.type === 'field') {
            // formFields entries: strings (Remedy) or {name, description} objects
            // from connector metadata (e.g. IS field ids)
            const fieldOptions = (formFields || []).map((f) => {
                const nf = typeof f === 'string' ? { name: f } : (f || { name: '' });
                return {
                    value: nf.name,
                    label: nf.description ? `${nf.name} — ${nf.description}` : nf.name
                };
            });
            return (
                <AutoComplete
                    value={v}
                    options={fieldOptions}
                    filterOption={(input, o) => `${o.value} ${o.label}`.toLowerCase().includes(input.toLowerCase())}
                    onChange={(val) => update(i, c.key, val)}
                    style={{ width: '100%' }}
                />
            );
        }
        return <Input value={v} onChange={(e) => update(i, c.key, e.target.value)} />;
    };

    // field / value flex; fixed columns (size) keep their width; + delete column
    const gridTemplate = columns.map((c) => (c.width ? `${c.width}px` : 'minmax(130px, 1fr)')).join(' ') + ' 40px';
    const rowGrid = { display: 'grid', gridTemplateColumns: gridTemplate, gap: 12, alignItems: 'center' };

    return (
        <div>
            {title && <div className="sc-panel-cap" style={{ margin: '0 0 10px' }}>{title}</div>}
            <div className="sc-rows">
                <div className="sc-row head" style={rowGrid}>
                    {columns.map((c) => <span key={c.key}>{c.title}</span>)}
                    <span />
                </div>
                {rows.length === 0 && (
                    <div className="sc-row" style={{ justifyContent: 'center', color: 'var(--sc-ink-3)' }}>
                        No fields yet — add one below.
                    </div>
                )}
                {rows.map((r, i) => (
                    <div key={i} className="sc-row" style={rowGrid}>
                        {columns.map((c) => <div key={c.key}>{renderCell(c, r, i)}</div>)}
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => remove(i)} aria-label="Remove field" />
                    </div>
                ))}
            </div>
            <Button icon={<PlusOutlined />} type="dashed" size="small" style={{ marginTop: 10 }} onClick={add}>
                {addLabel || 'add field'}
            </Button>
        </div>
    );
};

export default FieldsEditor;
